import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  LuArrowLeft,
  LuBan,
  LuClipboardList,
  LuDownload,
  LuFolder,
  LuReceipt,
  LuShieldCheck,
} from 'react-icons/lu';
import {
  cancelContract,
  getContract,
  listContractPayments,
  payWaterRight,
  revertWaterRight,
  receiptUrl,
  setTitlingStage,
  type ContractDetail as ContractDetailType,
  type PaymentRow,
  type TitlingStage,
} from '@/lib/api';
import { useMe } from '@/lib/MeContext';
import { useProject } from '@/lib/ProjectContext';
import { toaster } from '@/components/ui/toaster';
import { ScheduleTable } from '@/components/contract/ScheduleTable';
import { PaymentForm } from '@/components/contract/PaymentDialog';
import { VoidPaymentDialog } from '@/components/contract/VoidPaymentDialog';
import { ReplaceReceiptButton } from '@/components/contract/ReplaceReceiptButton';
import { TitlingStageBadge, TitlingStageSelect } from '@/components/contract/titling-stage';
import { exportContractStatementPDF } from '@/utils/pdf';
import { exportPaymentReceiptPDF } from '@/utils/receipt';
import { DocumentsPanel } from '@/components/documents/DocumentsPanel';
import { fmtDate, fmtMoney, fmtPct } from '@/utils/format';

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  cash: 'Efectivo',
  transfer: 'Transferencia',
  deposit: 'Depósito',
  check: 'Cheque',
};

const METHOD_LABEL = { french: 'Francés', german: 'Alemán' } as const;
const STATUS_LABEL = {
  draft: 'Borrador',
  active: 'Activo',
  completed: 'Completado',
  cancelled: 'Cancelado',
} as const;

function statusLabel(c: ContractDetailType): string {
  if (c.saleType === 'cash' && c.status === 'completed') return 'Pagado';
  return STATUS_LABEL[c.status];
}

export function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const [searchParams] = useSearchParams();
  const wantPago = searchParams.get('pago') === '1';
  const [contract, setContract] = useState<ContractDetailType | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [showForm, setShowForm] = useState(wantPago);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [waterRightBusy, setWaterRightBusy] = useState(false);
  const [titlingBusy, setTitlingBusy] = useState(false);

  function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    getContract(id)
      .then(setContract)
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudo cargar el contrato'))
      .finally(() => setLoading(false));
  }

  function loadPayments() {
    if (!id) return;
    listContractPayments(id)
      .then(setPayments)
      .catch(() => setPayments([]));
  }

  useEffect(load, [id]);
  useEffect(loadPayments, [id]);

  async function handleCancel() {
    if (!contract) return;
    const isCashUnwind = contract.status === 'completed' && contract.saleType === 'cash';
    const confirmMsg = isCashUnwind
      ? `¿Cancelar el contrato ${contract.contractNumber}? Se anulará el pago registrado y se liberará el terreno. Esta acción no se puede deshacer.`
      : `¿Cancelar el contrato ${contract.contractNumber}? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) {
      return;
    }
    setCancelling(true);
    try {
      await cancelContract(contract.id);
      toaster.create({ title: 'Contrato cancelado', type: 'success' });
      load();
      loadPayments();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo cancelar';
      toaster.create({ title: 'No se pudo cancelar', description: msg, type: 'error' });
    } finally {
      setCancelling(false);
    }
  }

  async function handlePayWaterRight() {
    if (!contract) return;
    if (!window.confirm('¿Marcar el derecho de agua como pagado?')) return;
    setWaterRightBusy(true);
    try {
      await payWaterRight(contract.id);
      toaster.create({ title: 'Derecho de agua marcado como pagado', type: 'success' });
      load();
    } catch (err) {
      toaster.create({ title: 'No se pudo actualizar', description: err instanceof Error ? err.message : '', type: 'error' });
    } finally {
      setWaterRightBusy(false);
    }
  }

  async function handleRevertWaterRight() {
    if (!contract) return;
    if (!window.confirm('¿Revertir el pago del derecho de agua?')) return;
    setWaterRightBusy(true);
    try {
      await revertWaterRight(contract.id);
      toaster.create({ title: 'Pago del derecho de agua revertido', type: 'success' });
      load();
    } catch (err) {
      toaster.create({ title: 'No se pudo actualizar', description: err instanceof Error ? err.message : '', type: 'error' });
    } finally {
      setWaterRightBusy(false);
    }
  }

  async function handleTitlingChange(next: TitlingStage | null) {
    if (!contract) return;
    setTitlingBusy(true);
    try {
      await setTitlingStage(contract.id, next);
      setContract({ ...contract, titlingStage: next });
      toaster.create({ title: 'Etapa de escrituración actualizada', type: 'success' });
    } catch (err) {
      toaster.create({
        title: 'No se pudo actualizar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setTitlingBusy(false);
    }
  }

  // Only blank the page on the initial load. A post-mutation refresh keeps the
  // rendered contract (and the active tab) mounted and updates data in place.
  if (loading && !contract) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <HStack gap="3" color="fg.muted">
          <Spinner size="sm" />
          <Text fontSize="sm">Cargando…</Text>
        </HStack>
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/contratos')} mb="3">
          <Icon as={LuArrowLeft} /> Volver a contratos
        </Button>
        <Text color="fg.muted">{error ?? 'Contrato no encontrado.'}</Text>
      </Box>
    );
  }

  const firstInstallment = contract.installments[0]?.totalAmount ?? '0';
  const isAdmin = me?.role === 'admin';
  const isCash = contract.saleType === 'cash';

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="5" maxW="6xl">
        <HStack gap="2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/contratos')}>
            <Icon as={LuArrowLeft} /> Contratos
          </Button>
          <Text fontSize="xs" color="fg.muted">
            / {contract.contractNumber}
          </Text>
        </HStack>

        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap="3">
          <Stack gap="1">
            <Text fontSize="xs" color="fg.muted">
              Contrato {contract.contractNumber} · {contract.buyerCode ?? '—'}
            </Text>
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              {contract.buyerName}
            </Heading>
            <HStack gap="2" mt="1">
              <Badge variant="outline">{isCash ? 'Contado' : `${METHOD_LABEL[contract.method]} · ${contract.dayCount}`}</Badge>
              <Badge colorPalette={contract.status === 'active' ? 'green' : isCash ? 'blue' : 'gray'}>
                {statusLabel(contract)}
              </Badge>
            </HStack>
            <Link asChild fontSize="sm" color="brand.600" alignSelf="flex-start" mt="1">
              <RouterLink to={`/clientes/${contract.buyerId}`}>Ver ficha del cliente</RouterLink>
            </Link>
          </Stack>
          <HStack gap="2" wrap="wrap">
            <Button variant="outline" size="sm" onClick={() => exportContractStatementPDF(contract, payments)}>
              <Icon as={LuDownload} /> Estado de cuenta PDF
            </Button>
            {isAdmin &&
              (contract.status === 'active' || (contract.status === 'completed' && isCash)) && (
                <Button variant="outline" size="sm" colorPalette="red" onClick={handleCancel} loading={cancelling}>
                  <Icon as={LuBan} /> Cancelar
                </Button>
              )}
          </HStack>
        </Stack>

        <SimpleGrid columns={{ base: 2, md: 4 }} gap="3">
          <KpiBox label="Saldo" value={fmtMoney(contract.balance?.outstanding ?? contract.financedAmount)} />
          <KpiBox label="Pagado" value={fmtMoney(contract.balance?.totalPaid ?? '0')} />
          {isCash ? (
            <>
              <KpiBox label="Precio de venta" value={fmtMoney(contract.financedAmount)} />
              <KpiBox label="Forma de pago" value={payments[0] ? (PAYMENT_METHOD_LABEL[payments[0].method] ?? payments[0].method) : '—'} />
            </>
          ) : (
            <>
              {Number(contract.balance?.moraOutstanding ?? '0') > 0 ? (
                <KpiBox label="Mora" value={fmtMoney(contract.balance?.moraOutstanding ?? '0')} />
              ) : (
                <KpiBox label="1ª cuota" value={fmtMoney(firstInstallment)} />
              )}
              {Number(contract.balance?.adminFeeOutstanding ?? '0') > 0 ? (
                <KpiBox label="Costo adm. pendiente" value={fmtMoney(contract.balance?.adminFeeOutstanding ?? '0')} />
              ) : (
                <KpiBox label="Tasa anual" value={fmtPct(contract.interestRateAnnual)} />
              )}
            </>
          )}
          {contract.waterRight?.status === 'due' && (
            <KpiBox label="Derecho de agua" value={fmtMoney(contract.waterRight.amount)} />
          )}
        </SimpleGrid>

        <Tabs.Root defaultValue={wantPago ? 'pagos' : 'plan'} variant="line">
          <Tabs.List>
            <Tabs.Trigger value="plan">
              <Icon as={LuClipboardList} /> Plan de pagos
            </Tabs.Trigger>
            <Tabs.Trigger value="pagos">
              <Icon as={LuReceipt} /> Pagos
            </Tabs.Trigger>
            <Tabs.Trigger value="datos">
              <Icon as={LuShieldCheck} /> Datos del contrato
            </Tabs.Trigger>
            <Tabs.Trigger value="documentos">
              <Icon as={LuFolder} /> Documentos
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="plan">
            <Stack gap="3" mt="3">
              <Text fontWeight="700">
                {isCash ? 'Pago de contado · 1 pago' : `Cronograma · ${contract.installments.length} cuotas`}
              </Text>
              <ScheduleTable rows={contract.installments} showPaid />
            </Stack>
          </Tabs.Content>

          <Tabs.Content value="pagos">
            <Stack gap="3" mt="3">
              <HStack justify="space-between">
                <Text fontWeight="700">{payments.length} pago(s)</Text>
                {contract.status === 'active' && (
                  <Button
                    size="sm"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    onClick={() => setShowForm((v) => !v)}
                  >
                    Registrar pago
                  </Button>
                )}
              </HStack>
              {showForm && (
                <PaymentForm
                  contract={contract}
                  onCancel={() => setShowForm(false)}
                  onDone={() => {
                    setShowForm(false);
                    load();
                    loadPayments();
                  }}
                />
              )}
              <PaymentsTable
                rows={payments}
                contract={contract}
                isAdmin={isAdmin}
                onChanged={() => {
                  load();
                  loadPayments();
                }}
              />
            </Stack>
          </Tabs.Content>

          <Tabs.Content value="datos">
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="4" mt="3">
              <DataCard
                title="Comprador"
                rows={[
                  ['Nombre', contract.buyerName],
                  ['Código', contract.buyerCode ?? '—'],
                  ['Documento', `${contract.buyerDocumentType.toUpperCase()} · ${contract.buyerDocumentNumber}`],
                  ['Vendedor', contract.sellerName ?? '—'],
                ]}
              />
              <DataCard
                title="Parámetros"
                rows={
                  isCash
                    ? [
                        ['Tipo de venta', 'Pago de contado'],
                        ['Precio de venta', fmtMoney(contract.financedAmount)],
                        ['Fecha de venta', fmtDate(contract.startDate)],
                      ]
                    : [
                        ['Monto financiado', fmtMoney(contract.financedAmount)],
                        ['Enganche', fmtMoney(contract.downPayment)],
                        ['Tasa anual', fmtPct(contract.interestRateAnnual)],
                        ['Plazo', `${contract.termMonths} meses`],
                        ['Método', METHOD_LABEL[contract.method]],
                        ['Inicio', fmtDate(contract.startDate)],
                      ]
                }
              />
              {contract.lot && (
                <DataCard
                  title="Terreno"
                  rows={[
                    ['Manzana', contract.lot.manzana],
                    ['Número', contract.lot.number],
                    ...(contract.lot.areaM2 ? [['Área', `${contract.lot.areaM2} m²`] as [string, string]] : []),
                    ...(contract.lot.listPrice
                      ? [['Precio de lista', fmtMoney(contract.lot.listPrice)] as [string, string]]
                      : []),
                  ]}
                />
              )}
              <WaterRightCard
                wr={contract.waterRight}
                isAdmin={isAdmin}
                busy={waterRightBusy}
                onPay={handlePayWaterRight}
                onRevert={handleRevertWaterRight}
              />
              <EscrituracionCard
                stage={contract.titlingStage}
                isAdmin={isAdmin}
                busy={titlingBusy}
                onChange={handleTitlingChange}
              />
            </SimpleGrid>
          </Tabs.Content>

          <Tabs.Content value="documentos">
            <DocumentsPanel parent={{ type: 'contract', id: contract.id }} canDelete={isAdmin} />
          </Tabs.Content>
        </Tabs.Root>
      </Stack>
    </Box>
  );
}

function KpiBox({ label, value }: { label: string; value: string }) {
  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="xl" p="4">
      <Text fontSize="2xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="800" letterSpacing="-0.01em">
        {value}
      </Text>
    </Box>
  );
}

function PaymentsTable({
  rows,
  contract,
  isAdmin,
  onChanged,
}: {
  rows: PaymentRow[];
  contract: ContractDetailType;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const { activeProject } = useProject();
  const [query, setQuery] = useState('');
  const [voidingPayment, setVoidingPayment] = useState<PaymentRow | null>(null);
  if (!rows.length) {
    return (
      <Text color="fg.muted" fontSize="sm">
        Sin pagos registrados.
      </Text>
    );
  }
  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter((p) =>
        [
          p.receiptNumber ?? '',
          p.reference ?? '',
          p.payerName ?? '',
          PAYMENT_METHOD_LABEL[p.method] ?? p.method,
          fmtDate(p.paymentDate),
          p.paymentDate,
          p.amount,
          fmtMoney(p.amount),
          p.paymentType === 'abono_capital' ? 'abono a capital' : '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    : rows;

  function handleExportReceipt(p: PaymentRow) {
    exportPaymentReceiptPDF({
      contract,
      payment: p,
      municipalityName: activeProject?.municipalityName ?? null,
    }).catch(() => {
      toaster.create({ title: 'No se pudo generar el recibo', type: 'error' });
    });
  }

  function appliedSummary(p: PaymentRow): string {
    const sum = (k: 'appliedToMora' | 'appliedToAdminFee' | 'appliedToInterest' | 'appliedToPrincipal') =>
      p.imputationApplied.lines.reduce((s, l) => s + Number(l[k] ?? '0'), 0);
    const parts: string[] = [];
    if (sum('appliedToMora') > 0) parts.push(`Mora ${fmtMoney(sum('appliedToMora').toFixed(2))}`);
    if (sum('appliedToAdminFee') > 0) parts.push(`C.adm ${fmtMoney(sum('appliedToAdminFee').toFixed(2))}`);
    if (sum('appliedToInterest') > 0) parts.push(`Int ${fmtMoney(sum('appliedToInterest').toFixed(2))}`);
    if (sum('appliedToPrincipal') > 0) parts.push(`Cap ${fmtMoney(sum('appliedToPrincipal').toFixed(2))}`);
    return parts.join(' · ') || '—';
  }

  return (
    <Stack gap="3">
      <HStack gap="3" wrap="wrap">
        <Input
          size="sm"
          maxW="sm"
          placeholder="Buscar recibo, referencia, fecha…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        {q && (
          <Text fontSize="xs" color="fg.muted">
            {filtered.length} de {rows.length}
          </Text>
        )}
      </HStack>
      {filtered.length === 0 ? (
        <Text color="fg.muted" fontSize="sm">
          Sin resultados para “{query}”.
        </Text>
      ) : (
        <>
          {/* Mobile: card list */}
          <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
            {filtered.map((p) => (
              <Box
                key={p.id}
                bg="bg.surface"
                borderWidth="1px"
                borderColor="border"
                rounded="lg"
                p="4"
                opacity={p.status === 'voided' ? 0.65 : 1}
              >
                <Stack gap="2">
                  <HStack justify="space-between" align="flex-start" gap="3">
                    <Stack gap="0.5" minW={0}>
                      <Text fontSize="xs" color="fg.muted" fontWeight="700">
                        {fmtDate(p.paymentDate)}
                      </Text>
                      <Text fontWeight="700" fontSize="lg">
                        {fmtMoney(p.amount)}
                      </Text>
                      <Text fontSize="sm" color="fg.muted">
                        {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                        {p.paymentType === 'abono_capital' ? ' · Abono a capital' : ''}
                      </Text>
                    </Stack>
                    <Stack gap="1" align="flex-end" flexShrink={0}>
                      <PaymentStatusBadges p={p} />
                    </Stack>
                  </HStack>
                  <Text fontSize="xs" color="fg.muted">
                    Aplicado: {appliedSummary(p)}
                  </Text>
                  {p.reference && (
                    <Text fontSize="sm" color="fg.muted">
                      Ref. {p.reference}
                    </Text>
                  )}
                  <HStack gap="3" wrap="wrap">
                    <Button size="xs" variant="outline" onClick={() => handleExportReceipt(p)}>
                      <Icon as={LuReceipt} /> Recibo{p.receiptNumber ? ` ${p.receiptNumber}` : ''}
                    </Button>
                    {p.hasReceipt && (
                      <Link href={receiptUrl(p.id)} target="_blank" rel="noreferrer" fontSize="sm" color="brand.600">
                        Ver comprobante
                      </Link>
                    )}
                  </HStack>
                  <PaymentRowActions p={p} isAdmin={isAdmin} onVoid={setVoidingPayment} onChanged={onChanged} />
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Desktop: table */}
          <Box
            borderWidth="1px"
            borderColor="border"
            rounded="md"
            overflow="hidden"
            overflowX="auto"
            display={{ base: 'none', md: 'block' }}
          >
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row bg="bg.subtle">
                  <Table.ColumnHeader>Fecha</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Monto</Table.ColumnHeader>
                  <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                  <Table.ColumnHeader>Método</Table.ColumnHeader>
                  <Table.ColumnHeader>Aplicado</Table.ColumnHeader>
                  <Table.ColumnHeader>Referencia</Table.ColumnHeader>
                  <Table.ColumnHeader>Recibo</Table.ColumnHeader>
                  <Table.ColumnHeader>Comprobante</Table.ColumnHeader>
                  <Table.ColumnHeader>Estado</Table.ColumnHeader>
                  <Table.ColumnHeader>Acciones</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((p) => (
                  <Table.Row key={p.id} opacity={p.status === 'voided' ? 0.65 : 1}>
                    <Table.Cell>{fmtDate(p.paymentDate)}</Table.Cell>
                    <Table.Cell textAlign="end">{fmtMoney(p.amount)}</Table.Cell>
                    <Table.Cell fontSize="xs">
                      {p.paymentType === 'abono_capital' ? (
                        <Badge colorPalette="purple" size="sm">Abono a capital</Badge>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                    <Table.Cell>{PAYMENT_METHOD_LABEL[p.method] ?? p.method}</Table.Cell>
                    <Table.Cell fontSize="xs" color="fg.muted">
                      {appliedSummary(p)}
                    </Table.Cell>
                    <Table.Cell>{p.reference ?? '—'}</Table.Cell>
                    <Table.Cell>
                      <Button size="xs" variant="outline" onClick={() => handleExportReceipt(p)}>
                        <Icon as={LuReceipt} /> Recibo{p.receiptNumber ? ` ${p.receiptNumber}` : ''}
                      </Button>
                    </Table.Cell>
                    <Table.Cell>
                      {p.hasReceipt ? (
                        <Link href={receiptUrl(p.id)} target="_blank" rel="noreferrer" color="brand.600">
                          Ver
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <PaymentStatusBadges p={p} />
                    </Table.Cell>
                    <Table.Cell>
                      <PaymentRowActions p={p} isAdmin={isAdmin} onVoid={setVoidingPayment} onChanged={onChanged} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </>
      )}
      {/* Always mounted (visibility driven by paymentId) rather than
          conditionally mounted while always-open — see VoidPaymentDialog's
          doc comment for why the previous pattern crashed the SPA. */}
      <VoidPaymentDialog
        paymentId={voidingPayment?.id ?? null}
        receiptNumber={voidingPayment?.receiptNumber}
        onClose={() => setVoidingPayment(null)}
        onDone={() => {
          setVoidingPayment(null);
          onChanged();
        }}
      />
    </Stack>
  );
}

// Hoisted to module scope (rather than nested inside PaymentsTable) so their
// component identity stays stable across PaymentsTable re-renders (e.g. every
// keystroke in the search box). A nested definition would get a fresh
// identity each render, forcing React to unmount/remount the subtree — which
// would silently close an already-open ReplaceReceiptButton dialog.
function PaymentStatusBadges({ p }: { p: PaymentRow }) {
  if (p.status !== 'voided' && !p.receiptReplacedAt) return <Text color="fg.muted">—</Text>;
  return (
    <HStack gap="1" wrap="wrap">
      {p.status === 'voided' && (
        <Badge colorPalette="red" title={p.voidReason ?? undefined}>
          Anulado
        </Badge>
      )}
      {p.receiptReplacedAt && <Badge colorPalette="gray">Reemplazado</Badge>}
    </HStack>
  );
}

function PaymentRowActions({
  p,
  isAdmin,
  onVoid,
  onChanged,
}: {
  p: PaymentRow;
  isAdmin: boolean;
  onVoid: (p: PaymentRow) => void;
  onChanged: () => void;
}) {
  const canVoid = isAdmin && p.paymentType === 'regular' && p.status !== 'voided';
  const canReplace = p.status !== 'voided';
  if (!canVoid && !canReplace) return <Text color="fg.muted">—</Text>;
  return (
    <HStack gap="2" wrap="wrap">
      {canVoid && (
        <Button size="xs" variant="ghost" colorPalette="red" onClick={() => onVoid(p)}>
          <Icon as={LuBan} /> Anular
        </Button>
      )}
      {canReplace && <ReplaceReceiptButton paymentId={p.id} onDone={onChanged} />}
    </HStack>
  );
}

function DataCard({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
      <Text fontWeight="700" mb="2">
        {title}
      </Text>
      <Stack gap="1.5" fontSize="sm">
        {rows.map(([k, v]) => (
          <HStack key={k} justify="space-between" gap="3">
            <Text color="fg.muted">{k}</Text>
            <Text textAlign="end">{v}</Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}

function WaterRightCard({
  wr,
  isAdmin,
  busy,
  onPay,
  onRevert,
}: {
  wr: ContractDetailType['waterRight'];
  isAdmin: boolean;
  busy: boolean;
  onPay: () => void;
  onRevert: () => void;
}) {
  if (!wr) return null;
  const na = wr.status === 'na';
  const paid = wr.status === 'paid';
  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
      <Text fontWeight="700" mb="2">
        Derecho de agua
      </Text>
      <Stack gap="2" fontSize="sm">
        <HStack justify="space-between" gap="3">
          <Text color="fg.muted">Monto</Text>
          <Text textAlign="end">{na ? 'No aplica' : fmtMoney(wr.amount)}</Text>
        </HStack>
        <HStack justify="space-between" gap="3">
          <Text color="fg.muted">Estado</Text>
          {na ? (
            <Badge colorPalette="gray">No aplica</Badge>
          ) : paid ? (
            <Badge colorPalette="green">Pagado{wr.paidAt ? ` · ${fmtDate(wr.paidAt)}` : ''}</Badge>
          ) : (
            <Badge colorPalette="orange">Pendiente</Badge>
          )}
        </HStack>
        {!na && wr.status === 'pending' && (
          <Text fontSize="xs" color="fg.muted">
            Se cobra al finalizar el plan.
          </Text>
        )}
        {isAdmin &&
          !na &&
          (paid ? (
            <Button variant="outline" size="xs" alignSelf="flex-start" loading={busy} onClick={onRevert}>
              Revertir
            </Button>
          ) : (
            <Button
              size="xs"
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{ bg: 'brand.700' }}
              alignSelf="flex-start"
              loading={busy}
              onClick={onPay}
            >
              Marcar pagado
            </Button>
          ))}
      </Stack>
    </Box>
  );
}

function EscrituracionCard({
  stage,
  isAdmin,
  busy,
  onChange,
}: {
  stage: TitlingStage | null;
  isAdmin: boolean;
  busy: boolean;
  onChange: (next: TitlingStage | null) => void;
}) {
  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
      <Text fontWeight="700" mb="2">
        Escrituración
      </Text>
      <Stack gap="2" fontSize="sm">
        <HStack justify="space-between" gap="3">
          <Text color="fg.muted">Etapa</Text>
          <TitlingStageBadge stage={stage} />
        </HStack>
        {isAdmin && <TitlingStageSelect value={stage} onChange={onChange} disabled={busy} />}
      </Stack>
    </Box>
  );
}
